import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Avatar, Box, CircularProgress, Link, TablePagination, Typography } from '@mui/material'
import ContentTypeChip from 'gui/components/common/ContentTypeChip'
import { getContentDetailHref } from 'services/content'
import { GET_TAGGED_CONTENT } from './queries'

const PAGE_SIZE_OPTIONS = [10, 25, 50]
const DEFAULT_PAGE_SIZE = 25

interface TaggedContentSectionProps {
  contentId?: number
  contentType?: string
  language?: string | null
  primaryLanguage?: string | null
}

/**
 * Displays a paginated list of content that references this tag.
 * Only mounts when the parent SectionAccordion is expanded,
 * so the GraphQL query fires lazily on first expand.
 */
function TaggedContentSection ({
  contentId,
  contentType,
  language,
  primaryLanguage,
}: TaggedContentSectionProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const listLanguage = resolveTaggedContentListLanguage(language, primaryLanguage)

  const { loading, error, data } = useQuery(GET_TAGGED_CONTENT, {
    variables: {
      contentId,
      contentType,
      offset: page * pageSize,
      limit: pageSize,
      language: listLanguage,
    },
    skip: !contentId || !contentType,
  })

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={28} />
        <Typography sx={{ ml: 1.5 }} color="text.secondary">
          Loading tagged content…
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 2, px: 1 }}>
        <Typography color="error">Failed to load tagged content: {error.message}</Typography>
      </Box>
    )
  }

  const contentList = data?.contentList
  const items = contentList?.content ?? []
  const totalCount = contentList?.count ?? 0

  if (totalCount === 0) {
    return (
      <Box sx={{ py: 2, px: 1 }}>
        <Typography color="text.secondary">No content is tagged with this tag.</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ px: 1 }}>
        {items.map((item: TaggedContentItem, index: number) => {
          if (!item) return null
          const thumbnailUrl = item.imageContentRelationships?.[0]?.image?.url
          const title = item.title || '(untitled)'
          const detailHref = getDetailHref(item)

          return (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                px: 1,
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f7f7f7',
                '&:hover': { backgroundColor: '#eef4fb' },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: item.published ? '#4caf50' : '#f44336',
                  flexShrink: 0,
                }}
              />

              {thumbnailUrl ? (
                <Box
                  component="img"
                  src={thumbnailUrl}
                  alt={title}
                  sx={{
                    width: 48,
                    height: 27,
                    objectFit: 'cover',
                    borderRadius: 0.5,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{ width: 48, height: 27, fontSize: 14, flexShrink: 0 }}
                >
                  {title.charAt(0).toUpperCase()}
                </Avatar>
              )}

              <Link
                href={detailHref}
                underline="hover"
                sx={{ fontSize: '0.875rem', minWidth: 60, flexShrink: 0 }}
                color="text.secondary"
              >
                {item.contentId}
              </Link>

              {item.contentType && (
                <ContentTypeChip
                  contentType={item.contentType}
                  label={item.contentType}
                  iconFontSize="0.75rem"
                  sx={{ flexShrink: 0 }}
                />
              )}

              <Link
                href={detailHref}
                underline="hover"
                sx={{
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Link>
            </Box>
          )
        })}
      </Box>

      {totalCount > PAGE_SIZE_OPTIONS[0] && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10))
            setPage(0)
          }}
        />
      )}
    </Box>
  )
}

/** contentList `language` filter: same as page content, with primaryLanguage fallback; null = do not filter */
function resolveTaggedContentListLanguage (
  language?: string | null,
  primaryLanguage?: string | null,
): string[] | null {
  const fromLanguage = language?.trim()
  if (fromLanguage) return [fromLanguage]
  const fromPrimary = primaryLanguage?.trim()
  if (fromPrimary) return [fromPrimary]
  return null
}

function getDetailHref (item: TaggedContentItem): string {
  const id = item.id ?? item.contentId
  if (id == null || item.contentType == null) return '#'
  return getContentDetailHref(id, item.contentType)
}

interface TaggedContentItem {
  id?: string | null
  contentId?: number | null
  contentType?: string | null
  title?: string | null
  published?: boolean | null
  imageContentRelationships?: Array<{
    type?: string | null
    image?: {
      id?: string | null
      url?: string | null
    } | null
  } | null> | null
}

export default TaggedContentSection
